import Button from 'common/components/entities/Button/button'
import { useCalculateButtonStyles } from 'src/publisher/shared/hooks/use-calculate-button-styles'
import { useSelectedButtonColor } from 'src/publisher/shared/hooks/use-theme-color/use-selected-button-color'
import { PaymentButtonInterface } from 'common/types/entities/PaymentButtonInterface'
import StyleWrapper from 'publisher/components/core/StyleWrapper'
import FieldErrors from 'publisher/components/FieldErrors'
import { useMercadoPago } from 'publisher/context/mercadoPagoContext'
import useMercadoPagoPaymentHandler from 'publisher/hooks/payment/useMercadoPagoPaymentHandler'
import ThreeDsModal, {
  ThreeDsInfo,
} from 'publisher/paymentProcessors/mercadoPago/components/ThreeDsModal'
import React, { useCallback, useEffect, useState } from 'react'

export interface MercadoPagoCreditCardPaymentButtonProps {
  entity: PaymentButtonInterface
}

const MercadoPagoCreditCardPaymentButton = ({
  entity,
}: MercadoPagoCreditCardPaymentButtonProps) => {
  const { errors, isLoading, setErrors, handleSubmit } =
    useMercadoPagoPaymentHandler(entity)
  const { setInitMercadoPago } = useMercadoPago()

  const [isThreeDsModalOpen, setIsThreeDsModalOpen] = useState(false)
  const [redirect, setRedirect] = useState<string>()
  const [orderBumpOffers, setOrderBumpOffers] = useState<number[]>([])
  const [threeDsInfo, setThreeDsInfo] = useState<ThreeDsInfo>({
    creq: '',
    external_resource_url: '',
  })

  useEffect(() => {
    setInitMercadoPago(true)
  }, [])

  const handleThreeDs = useCallback(
    (threeDsData: ThreeDsInfo, bumpOffers: number[], redirect?: string) => {
      setThreeDsInfo(threeDsData)
      setRedirect(redirect)
      setOrderBumpOffers(bumpOffers)
      setIsThreeDsModalOpen(true)
    },
    [],
  )

  const onThreeDsClose = useCallback(
    (errors?: string[]) => {
      setIsThreeDsModalOpen(false)
      if (errors && errors.length) {
        setErrors(errors)
      }
    },
    [setErrors],
  )

  const handleMercadoSubmit = useCallback(
    (e: React.SyntheticEvent) => handleSubmit(e, handleThreeDs),
    [handleSubmit, handleThreeDs],
  )

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    // calculatedTextAlign,
    // calculatedLetterSpacing,
    calculatedFontFamily,
    calculatedFontWeight,
    calculatedFontStyle,
  } = useCalculateButtonStyles({
    border: entity.border,
    mobileBorder: entity.mobileBorder,
    padding: entity.padding,
    mobilePadding: entity.mobilePadding,
    buttonType: entity.buttonType,
    fontSize: entity.textFontSize,
    mobileFontSize: entity.mobileTextFontSize,
    lineHeight: entity.lineHeight,
    mobileLineHeight: entity.mobileLineHeight,
    fontFamily: entity.textFontFamily,
    mobileFontFamily: entity.mobileTextFontFamily,
    fontWeight: entity.textFontWeight,
    mobileFontWeight: entity.mobileTextFontWeight,
    fontStyle: entity.textFontStyle,
    mobileFontStyle: entity.mobileTextFontStyle,
  })

  const {
    buttonTextColor,
    buttonBackgroundColor,
    hoveredButtonBackgroundColor,
  } = useSelectedButtonColor(entity.buttonType)

  return (
    <StyleWrapper
      flex
      flexColumn
      margin={entity.margin}
      mobileMargin={entity.mobileMargin}
      appearance={entity.appearance}
    >
      <Button
        onClick={handleMercadoSubmit}
        disabled={isLoading}
        loading={isLoading}
        loadingText={entity.loadingText}
        attrId={entity.htmlAttrId}
        text={entity.text}
        subText={entity.subText}
        width={entity.width}
        mobileWidth={entity.mobileWidth}
        padding={calculatedPadding.desktop}
        mobilePadding={calculatedPadding.mobile}
        border={calculatedBorder.desktop}
        mobileBorder={calculatedBorder.mobile}
        alignSelf={entity.alignSelf}
        mobileAlignSelf={entity.mobileAlignSelf}
        textColor={entity.textColor || buttonTextColor}
        mobileTextColor={entity.mobileTextColor || buttonTextColor}
        textFontSize={calculatedFontSize.desktop}
        mobileTextFontSize={calculatedFontSize.mobile}
        lineHeight={calculatedLineHeight.desktop}
        mobileLineHeight={calculatedLineHeight.mobile}
        textFontFamily={calculatedFontFamily.desktop}
        textFontStyle={calculatedFontStyle.desktop}
        textFontWeight={calculatedFontWeight.desktop}
        subTextFontSize={entity.subTextFontSize}
        mobileSubTextFontSize={entity.mobileSubTextFontSize}
        subTextColor={entity.subTextColor}
        mobileSubTextColor={entity.mobileSubTextColor}
        subTextFontFamily={entity.subTextFontFamily}
        subTextFontStyle={entity.subTextFontStyle}
        subTextFontWeight={entity.subTextFontWeight}
        boxShadow={entity.boxShadow}
        mobileBoxShadow={entity.mobileBoxShadow}
        hover={{
          ...entity.hover,
          backgroundColor:
            entity?.hover?.backgroundColor || hoveredButtonBackgroundColor,
        }}
        mobileHover={{
          ...entity.mobileHover,
          backgroundColor:
            entity?.mobileHover?.backgroundColor ||
            entity?.hover?.backgroundColor ||
            hoveredButtonBackgroundColor,
        }}
        background={
          (entity.background ?? entity.backgroundColor) || buttonBackgroundColor
        }
        mobileBackground={
          (entity.mobileBackground ?? entity.mobileBackgroundColor) ||
          buttonBackgroundColor
        }
        iconClassNameBefore={entity.iconClassNameBefore}
        iconClassNameAfter={entity.iconClassNameAfter}
      />
      <FieldErrors errors={errors} align="center" />
      {isThreeDsModalOpen && (
        <ThreeDsModal
          creq={threeDsInfo.creq}
          external_resource_url={threeDsInfo.external_resource_url}
          redirect={redirect}
          orderBumpOffers={orderBumpOffers}
          onClose={onThreeDsClose}
        />
      )}
    </StyleWrapper>
  )
}

export default MercadoPagoCreditCardPaymentButton
